import { Metrics, logger } from '@moonpig/web-core-monitoring'
import { DateTime } from 'luxon'
import { AuthenticatedFetch } from '@moonpig/web-core-fetch'
import { getDataInferenceUrl } from './getDataInferenceUrl'

type Promotion = {
  name: string
  probability: number
}

export type DynamicHeroResponse = {
  promotions: Promotion[]
  seeding?: boolean
  status?: number
}

const sortPromotions = (promotions: Promotion[]) => {
  return promotions.sort(
    (a: Promotion, b: Promotion) => b.probability - a.probability,
  )
}

const getHeaders = ({
  isMobile,
  homePageDynamicHeroModel,
}: {
  isMobile: boolean
  homePageDynamicHeroModel?: string
}) => {
  const headers: {
    'mnpg-client-name': string
    'mnpg-is-mobile-browser': string
    'mnpg-experiments'?: string
  } = {
    'mnpg-client-name': 'web',
    'mnpg-is-mobile-browser': isMobile.toString(),
  }

  if (homePageDynamicHeroModel !== 'disabled') {
    headers[
      'mnpg-experiments'
    ] = `homepage-dynamic-hero-model=${homePageDynamicHeroModel}`
  }

  return headers
}

const fetchDynamicHeroPromotions = async ({
  isMobile,
  fetch,
  homePageDynamicHeroModel,
}: {
  isMobile: boolean
  fetch: AuthenticatedFetch
  homePageDynamicHeroModel: string
}) => {
  const dataInferenceUrl = getDataInferenceUrl()
  const date = DateTime.local().toISO()

  const headers = getHeaders({
    isMobile,
    homePageDynamicHeroModel,
  })

  let response = null
  try {
    response = await fetch(
      `${dataInferenceUrl}/dynamic-hero?timestamp=${encodeURIComponent(
        date,
      )}&limit=5`,
      {
        credentials: 'include',
        timeout: 500,
        headers,
      },
    )
  } catch (error) {
    logger.fixToday('Error thrown while querying Data Inference', {}, error)
    throw error
  }

  if (response.ok) return response

  logger.fixToday('Failed to query Data Inference', {
    response: await response.text(),
    code: response.status,
  })

  throw new Error('Failed to query Data Inference')
}

export const getDynamicHeroPromotionVariants = async (
  fetch: AuthenticatedFetch,
  {
    isMobile,
    metrics,
    homePageDynamicHeroModel,
  }: {
    isMobile: boolean
    metrics: Metrics
    homePageDynamicHeroModel: string
  },
): Promise<DynamicHeroResponse> => {
  try {
    const response = await metrics.traceAsync(
      {
        traceName: 'data-inference-request',
        metricName: 'data-inference-request',
        metricDimensions: {
          action: 'getDynamicHeroPromotionVariants',
        },
      },
      () =>
        fetchDynamicHeroPromotions({
          isMobile,
          fetch,
          homePageDynamicHeroModel,
        }),
    )

    const responseJson = await response.json()
    return {
      promotions: sortPromotions(responseJson.promotions),
      seeding: responseJson.metadata?.seeding,
    }
  } catch {
    return { promotions: [] }
  }
}
